import {
  ADDON_PRODUCT_BASE_TYPE,
  ADDON_PRODUCT_DWH_RENT_TYPE,
  ADDON_PRODUCT_DWH_TYPE,
  ADDON_PRODUCT_ETL_CONNECTIONS_TYPE,
  ADDON_PRODUCT_METABASE_AI_MANAGED_TYPE,
  ADDON_PRODUCT_METABASE_AI_TIERED_TYPE,
  ADDON_PRODUCT_METABASE_AI_TYPE,
  ADDON_PRODUCT_PRODUCT_ANALYTICS_FREE_TYPE,
  ADDON_PRODUCT_PRODUCT_ANALYTICS_TYPE,
  ADDON_PRODUCT_TRANSFORMS_ADVANCED_METERED_TYPE,
  ADDON_PRODUCT_TRANSFORMS_ADVANCED_TYPE,
  ADDON_PRODUCT_TRANSFORMS_BASIC_METERED_TYPE,
  ADDON_PRODUCT_TRANSFORMS_BASIC_TYPE,
} from "@stores/addons/constants";
import {
  ISubscription,
  AddonSubscribedProductType,
  SubscribedProductsType,
} from "./types";
import { formatQuantity } from "@common/utils/string";
import { getTransformsLabel } from "@stores/addons/utils";
import { SUBSCRIPTION_STATUS_TRIAL } from "./constants";

export const isSubscriptionInTrial = ({
  in_trial,
  status,
}: Pick<ISubscription, "in_trial" | "status">) => {
  return in_trial || status === SUBSCRIPTION_STATUS_TRIAL;
};

export function getAddonSubscribedProducts(
  subscribedProducts: SubscribedProductsType,
): AddonSubscribedProductType[] {
  return subscribedProducts.filter(
    (subscribedProduct) =>
      subscribedProduct.product_type !== ADDON_PRODUCT_BASE_TYPE,
  ) as AddonSubscribedProductType[];
}

const USAGE_BASED_ADDON_PRODUCT_TYPES: AddonSubscribedProductType["product_type"][] =
  [
    ADDON_PRODUCT_DWH_RENT_TYPE,
    ADDON_PRODUCT_TRANSFORMS_BASIC_METERED_TYPE,
    ADDON_PRODUCT_TRANSFORMS_ADVANCED_METERED_TYPE,
    ADDON_PRODUCT_METABASE_AI_MANAGED_TYPE,
    // Paid Metabase Track only. Deliberately omits
    // `ADDON_PRODUCT_PRODUCT_ANALYTICS_FREE_TYPE`: the free variant has no
    // Stripe price, so it never bills and must not suppress the next-payment
    // amount. Don't "complete" this list by adding it.
    ADDON_PRODUCT_PRODUCT_ANALYTICS_TYPE,
  ];

export function hasUsageBasedAddOn({
  subscribed_products,
}: Pick<ISubscription, "subscribed_products">): boolean {
  return getAddonSubscribedProducts(subscribed_products).some(
    ({ product_type }) =>
      USAGE_BASED_ADDON_PRODUCT_TYPES.includes(product_type),
  );
}

const createFindAddOnByProductType =
  (productType: AddonSubscribedProductType["product_type"]) =>
  (subscription: Pick<ISubscription, "subscribed_products">) =>
    subscription.subscribed_products.find(
      (addOn) => addOn.product_type === productType,
    ) as AddonSubscribedProductType | undefined;

export const isAddOnInTrial = (
  { trial_ends_at }: Pick<AddonSubscribedProductType, "trial_ends_at">,
  subscription: Pick<ISubscription, "in_trial" | "status">,
) =>
  isSubscriptionInTrial(subscription) ||
  (!!trial_ends_at && new Date(trial_ends_at) > new Date());

export const getAddOnTrialDate = (
  addOn: Pick<AddonSubscribedProductType, "trial_ends_at">,
  subscription: Pick<ISubscription, "in_trial" | "status" | "trial_ends_at">,
) =>
  isAddOnInTrial(addOn, subscription)
    ? addOn.trial_ends_at || subscription.trial_ends_at
    : undefined;

/**
 * prepaid storage add-on
 */
export const getSubscriptionStorageAddOn = createFindAddOnByProductType(
  ADDON_PRODUCT_DWH_TYPE,
);

/**
 * usage-based storage add-on
 */
export const getStorageRentAddOn = createFindAddOnByProductType(
  ADDON_PRODUCT_DWH_RENT_TYPE,
);

/**
 * whichever storage add-on is on the subscription — a subscription carries
 * either the prepaid one or the usage-based one, never both
 */
export const getAnyStorageAddOn = (
  subscription: Pick<ISubscription, "subscribed_products">,
): AddonSubscribedProductType | undefined =>
  getSubscriptionStorageAddOn(subscription) ??
  getStorageRentAddOn(subscription);

/**
 * paid Metabase Track add-on
 */
export const getProductAnalyticsAddOn = createFindAddOnByProductType(
  ADDON_PRODUCT_PRODUCT_ANALYTICS_TYPE,
);

/**
 * free Metabase Track add-on, granted through the internal tools API only
 */
export const getProductAnalyticsFreeAddOn = createFindAddOnByProductType(
  ADDON_PRODUCT_PRODUCT_ANALYTICS_FREE_TYPE,
);

/**
 * whichever Metabase Track variant is on the subscription — harbormaster keeps
 * the paid and free variants mutually exclusive
 */
export const getAnyProductAnalyticsAddOn = (
  subscription: Pick<ISubscription, "subscribed_products">,
): AddonSubscribedProductType | undefined =>
  getProductAnalyticsAddOn(subscription) ??
  getProductAnalyticsFreeAddOn(subscription);

export const getMetabotAddOn = createFindAddOnByProductType(
  ADDON_PRODUCT_METABASE_AI_TIERED_TYPE,
);

export const getMetabaseAIServiceAddOn = createFindAddOnByProductType(
  ADDON_PRODUCT_METABASE_AI_MANAGED_TYPE,
);

export function getTransformsAddOn({
  subscribed_products,
}: Pick<ISubscription, "subscribed_products">):
  | AddonSubscribedProductType
  | undefined {
  const transforms = getAddonSubscribedProducts(subscribed_products).filter(
    ({ product_type }) =>
      product_type === ADDON_PRODUCT_TRANSFORMS_BASIC_TYPE ||
      product_type === ADDON_PRODUCT_TRANSFORMS_ADVANCED_TYPE ||
      product_type === ADDON_PRODUCT_TRANSFORMS_BASIC_METERED_TYPE ||
      product_type === ADDON_PRODUCT_TRANSFORMS_ADVANCED_METERED_TYPE,
  );

  // first transforms add-on only
  return transforms.length ? transforms[0] : undefined;
}

interface IGetPrettyAddonRes
  extends Pick<AddonSubscribedProductType, "product_type"> {
  title: string;
  quantity: string;
}

export function getPrettyAddon({
  product_type,
  prepaid_units,
}: Pick<
  AddonSubscribedProductType,
  "product_type" | "prepaid_units"
>): IGetPrettyAddonRes {
  // Storage
  if (product_type === ADDON_PRODUCT_DWH_TYPE) {
    return {
      product_type,
      title: "Storage",
      quantity: `${formatQuantity(prepaid_units)} stored rows`,
    };
  }
  // Storage (usage-based)
  else if (product_type === ADDON_PRODUCT_DWH_RENT_TYPE) {
    return {
      product_type,
      title: "Storage",
      quantity: "Usage-based billing",
    };
  }
  // Metabase Track
  else if (product_type === ADDON_PRODUCT_PRODUCT_ANALYTICS_TYPE) {
    return {
      product_type,
      title: "Metabase Track",
      quantity: "Usage-based billing",
    };
  }
  // Metabase Track (free)
  else if (product_type === ADDON_PRODUCT_PRODUCT_ANALYTICS_FREE_TYPE) {
    return { product_type, title: "Metabase Track", quantity: "Included" };
  }
  // Transforms
  else if (
    product_type === ADDON_PRODUCT_TRANSFORMS_BASIC_TYPE ||
    product_type === ADDON_PRODUCT_TRANSFORMS_ADVANCED_TYPE ||
    product_type === ADDON_PRODUCT_TRANSFORMS_BASIC_METERED_TYPE ||
    product_type === ADDON_PRODUCT_TRANSFORMS_ADVANCED_METERED_TYPE
  ) {
    return {
      product_type,
      title: "Transforms",
      quantity: getTransformsLabel(product_type),
    };
  }
  // ETL
  else if (product_type === ADDON_PRODUCT_ETL_CONNECTIONS_TYPE) {
    return { product_type, title: "ETL Connections", quantity: "" };
  }
  // Metabase AI
  else if (product_type === ADDON_PRODUCT_METABASE_AI_TIERED_TYPE) {
    return {
      product_type,
      title: "Metabot AI",
      quantity: `${formatQuantity(prepaid_units)} requests`,
    };
  }
  // Metabase AI Service
  else if (product_type === ADDON_PRODUCT_METABASE_AI_MANAGED_TYPE) {
    return {
      product_type,
      title: "Metabase AI Service",
      quantity: "Usage-based billing",
    };
  }
  // Free Metabot AI
  else if (product_type === ADDON_PRODUCT_METABASE_AI_TYPE) {
    return {
      product_type,
      title: "Free Metabot AI",
      quantity: "Promo period",
    };
  }

  return { product_type, title: product_type, quantity: `${prepaid_units}` };
}

export function getPrettyAddons({
  subscribed_products,
}: Pick<ISubscription, "subscribed_products">): IGetPrettyAddonRes[] {
  return getAddonSubscribedProducts(subscribed_products).map(getPrettyAddon);
}

interface IPayAsYouGoMeterValues {
  billableUnits: number;
  includedUnitsUsed: number;
}

interface IPayAsYouGoMeterValuesParams {
  meterValue: number;
  freeUnits: number;
  meterFreeUnits: number | null | undefined;
}

// Harbormaster contract for `pay_as_you_go` metering (`update-meter`,
// `period-free-units`, Stripe sync) — shared by `metabase-ai-managed` and
// `product-analytics`:
// - `meterValue`: MPU sum for the current billing period.
// - `freeUnits`: lifetime free allowance (cross-period pool on the addon).
// - `meterFreeUnits`: remaining free pool allocatable against this period,
//   `max(0, freeUnits − usage_before_this_period)`. Billable usage is
//   `meterValue − meterFreeUnits` (floored at 0).
//
// Lifetime draw on the free pool is `meterValue + freeUnits − meterFreeUnits`
// when `meterFreeUnits` is set; included use for the graph is that draw capped
// at `freeUnits`.
//
// `meterFreeUnits` may be `null` before Harbormaster materializes meter fields;
// we treat that like first-period semantics (no prior draw on the pool).
//
// Note this does not apply to `dwh-rent`, whose `meter_strategy` is `rent`: it
// bills the live footprint against `included_units` instead.
export function getPayAsYouGoMeterValues({
  meterValue,
  freeUnits,
  meterFreeUnits,
}: IPayAsYouGoMeterValuesParams): IPayAsYouGoMeterValues {
  // no prior draw on the pool when Harbormaster has not materialized it yet
  const remainingFreePool = meterFreeUnits ?? freeUnits;

  return {
    billableUnits: Math.max(0, meterValue - remainingFreePool),
    includedUnitsUsed: Math.max(
      0,
      Math.min(freeUnits, meterValue + freeUnits - remainingFreePool),
    ),
  };
}

export function getAddonsPrice({
  subscribed_products,
}: Pick<ISubscription, "subscribed_products">): number {
  const prices = getAddonSubscribedProducts(subscribed_products).map(
    ({ base_fee, total_units, included_units, price_per_unit, is_metered }) => {
      if (is_metered) {
        return base_fee;
      }
      return base_fee + (total_units - included_units) * price_per_unit;
    },
  );

  return prices.length ? prices.reduce((acc, price) => acc + price) : 0;
}
